import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../hooks/useApi';

const resolveAvatarUrl = (url, baseUrl) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (!baseUrl) return url;
    return `${baseUrl}${url}`;
};

const isImageUrl = (url) => /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(url || '');

const formatMarketplaceType = (value) => {
    if (!value) return '';
    if (value === 'ozon') return 'Ozon';
    if (value === 'wb') return 'Wildberries';
    return value.toString();
};

const clampPercent = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    return Math.min(100, Math.max(0, num));
};

const PublicProfile = ({ slug, openLightbox }) => {
    const { API_BASE_URL } = useApi();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [caseStudyImageIdx, setCaseStudyImageIdx] = useState({});

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setError(null);
        fetch(`${API_BASE_URL}/api/v1/users/public/${slug}`)
            .then(async (response) => {
                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(text || 'Profile not found');
                }
                return response.json();
            })
            .then((data) => {
                setProfile(data);
            })
            .catch((err) => {
                setError(err.message || 'Profile not found');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [slug, API_BASE_URL]);

    const initials = useMemo(() => {
        if (!profile) return '';
        const first = (profile.first_name || '').trim();
        const last = (profile.last_name || '').trim();
        const initialsValue = `${first.charAt(0)}${last.charAt(0)}`.trim();
        if (initialsValue) return initialsValue.toUpperCase();
        return (profile.display_name || '?').charAt(0).toUpperCase();
    }, [profile]);
    const avatarUrl = useMemo(
        () => resolveAvatarUrl(profile?.avatar_url || '', API_BASE_URL),
        [profile?.avatar_url, API_BASE_URL]
    );
    const onTimePercent = useMemo(
        () => clampPercent(profile?.dashboards?.tasks_on_time_percent ?? 0),
        [profile?.dashboards?.tasks_on_time_percent]
    );

    const certificateUrls = useMemo(
        () => profile?.certificates || [],
        [profile?.certificates]
    );
    const certificateImages = useMemo(
        () => certificateUrls.filter((url) => isImageUrl(url)),
        [certificateUrls]
    );
    const certificateIndex = useMemo(
        () => new Map(certificateImages.map((url, index) => [url, index])),
        [certificateImages]
    );

    const handleCertificateOpen = (url) => {
        if (!isImageUrl(url) || !openLightbox) {
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
        }
        const index = certificateIndex.get(url) ?? 0;
        openLightbox(certificateImages, index);
    };

    if (loading) {
        return (
            <div className="panel public-profile">
                <div className="empty">Загрузка профиля...</div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="panel public-profile">
                <div className="empty">Профиль не найден.</div>
            </div>
        );
    }

    return (
        <div className="public-profile">
            <div className="panel public-profile__header">
                <div className="public-profile__hero">
                    <div className="public-profile__avatar">
                        {avatarUrl ? (
                            <img src={avatarUrl} alt={profile.display_name} />
                        ) : (
                            initials
                        )}
                    </div>
                    <div>
                        <h2>{profile.display_name}</h2>
                        {profile.agency_name && <div className="muted">{profile.agency_name}</div>}
                        <div className="public-profile__badges">
                            <span className={`badge ${profile.verified_identity ? 'badge--green' : ''}`}>
                                Авторизованный пользователь
                            </span>
                            <span className={`badge ${profile.anylex_recommended ? 'badge--red' : ''}`}>
                                Энилекс рекомендует
                            </span>
                        </div>
                    </div>
                </div>
                {profile.bio && <p className="public-profile__bio">{profile.bio}</p>}
                <div className="public-profile__contacts">
                    {profile.email && <div>Email: {profile.email}</div>}
                    {profile.phone && <div>Телефон: {profile.phone}</div>}
                    {profile.telegram_id && <div>Telegram: {profile.telegram_id}</div>}
                </div>
                {certificateUrls.length ? (
                    <div className="public-profile__certs">
                        <div className="muted">Сертификаты</div>
                        <div className="public-profile__cert-thumbs">
                            {certificateUrls.map((url) => {
                                if (!isImageUrl(url)) {
                                    return (
                                        <a
                                            key={url}
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="public-profile__cert-file"
                                        >
                                            PDF
                                        </a>
                                    );
                                }
                                return (
                                    <button
                                        key={url}
                                        type="button"
                                        className="public-profile__cert-thumb"
                                        onClick={() => handleCertificateOpen(url)}
                                    >
                                        <img src={url} alt="Сертификат" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : null}
            </div>

            {(profile.show_dashboard_projects || profile.show_dashboard_cabinets) && (
                <div className="public-profile__grid">
                    {profile.show_dashboard_projects && (
                        <div className="panel">
                            <div className="panel__head">
                                <h4>Проекты</h4>
                            </div>
                            {profile.projects?.length ? (
                                <div className="public-profile__list">
                                    {profile.projects.map((item) => (
                                        <div key={item.id} className="public-profile__list-item">
                                            {item.name}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="muted">Нет проектов.</div>
                            )}
                        </div>
                    )}
                    {profile.show_dashboard_cabinets && (
                        <div className="panel">
                            <div className="panel__head">
                                <h4>Кабинеты</h4>
                            </div>
                            {profile.cabinets?.length ? (
                                <div className="public-profile__list">
                                    {profile.cabinets.map((item) => (
                                        <div key={item.id} className="public-profile__list-item">
                                            {item.name}
                                            {item.marketplace_type ? ` — ${formatMarketplaceType(item.marketplace_type)}` : ''}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="muted">Нет кабинетов.</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="public-profile__dashboards">
                {profile.show_dashboard_projects && (
                    <div className="panel">
                        <div className="panel__head">
                            <h4>Дашборд проектов и кабинетов</h4>
                        </div>
                        <div className="public-profile__metrics">
                            <div>
                                <span className="muted">Проекты</span>
                                <strong>{profile.dashboards?.projects_count ?? 0}</strong>
                            </div>
                            <div>
                                <span className="muted">Кабинеты</span>
                                <strong>{profile.dashboards?.cabinets_count ?? 0}</strong>
                            </div>
                        </div>
                    </div>
                )}
                {profile.show_dashboard_cabinets && (
                    <div className="panel">
                        <div className="panel__head">
                            <h4>Дашборд задач</h4>
                        </div>
                        <div className="public-profile__metrics">
                            <div>
                                <span className="muted">Всего задач</span>
                                <strong>{profile.dashboards?.tasks_total_count ?? 0}</strong>
                            </div>
                            <div>
                                <span className="muted">В срок</span>
                                <strong>{profile.dashboards?.tasks_on_time_count ?? 0}</strong>
                            </div>
                            <div>
                                <span className="muted">Процент</span>
                                <strong>{profile.dashboards?.tasks_on_time_percent ?? 0}%</strong>
                            </div>
                        </div>
                        <div className="metric-bar" aria-hidden="true">
                            <span style={{ width: `${onTimePercent}%` }} />
                        </div>
                        </div>
                    )}
            </div>

            {profile.case_studies && profile.case_studies.length > 0 && (
                <div className="panel">
                    <div className="panel__head">
                        <h4>Примеры работ</h4>
                    </div>
                    <div className="public-profile__case-studies-grid">
                        {profile.case_studies.map((caseStudy) => (
                            <div key={caseStudy.id} className="public-profile__case-study-card">
                                {(caseStudy.images || []).length > 0 && (
                                    <div className="public-profile__case-study-image">
                                        <img
                                            src={`${API_BASE_URL}${caseStudy.images[caseStudyImageIdx[caseStudy.id] || 0]}`}
                                            alt={caseStudy.title}
                                        />
                                        {caseStudy.images.length > 1 && (
                                            <div className="public-profile__case-study-nav">
                                                <button
                                                    type="button"
                                                    className="ghost ghost--sm"
                                                    onClick={() =>
                                                        setCaseStudyImageIdx((prev) => ({
                                                            ...prev,
                                                            [caseStudy.id]: Math.max(0, (prev[caseStudy.id] || 0) - 1),
                                                        }))
                                                    }
                                                    disabled={(caseStudyImageIdx[caseStudy.id] || 0) === 0}
                                                >
                                                    ←
                                                </button>
                                                <span className="public-profile__case-study-counter">
                                                    {(caseStudyImageIdx[caseStudy.id] || 0) + 1} / {caseStudy.images.length}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="ghost ghost--sm"
                                                    onClick={() =>
                                                        setCaseStudyImageIdx((prev) => ({
                                                            ...prev,
                                                            [caseStudy.id]: Math.min(
                                                                caseStudy.images.length - 1,
                                                                (prev[caseStudy.id] || 0) + 1
                                                            ),
                                                        }))
                                                    }
                                                    disabled={
                                                        (caseStudyImageIdx[caseStudy.id] || 0) ===
                                                        caseStudy.images.length - 1
                                                    }
                                                >
                                                    →
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="public-profile__case-study-content">
                                    <strong>{caseStudy.title}</strong>
                                    {caseStudy.client_name && <div className="muted">{caseStudy.client_name}</div>}
                                    {caseStudy.category && <span className="badge">{caseStudy.category}</span>}
                                    {caseStudy.revenue_increase && (
                                        <span className="badge badge--green">+{caseStudy.revenue_increase}%</span>
                                    )}
                                    {caseStudy.description && <p className="muted">{caseStudy.description}</p>}
                                    {caseStudy.result && <div className="public-profile__case-study-result">{caseStudy.result}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicProfile;
